import { convertToInteger } from './../../../../modules/shared/validators/convert-to-integer.validator';
import { convertToAbsolute } from './../../../../modules/shared/validators/convert-to-absolute.validator';
import { WorkflowModelsNS } from './../../models/add-update-workflow.models';
import { Subject, takeUntil } from 'rxjs';
import { AppMainComponent } from './../../../../app.main.component';
import { FormErrorHandler } from './../../../../modules/shared/helpers/form-error-handler.helper';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ParseHelper } from 'src/app/modules/shared/parse.helper';

@Component({
  selector: 'app-workflow-trigger-time-dialog',
  templateUrl: './workflow-trigger-time-dialog.component.html',
  styleUrls: ['./workflow-trigger-time-dialog.component.scss']
})
export class WorkflowTriggerTimeDialogComponent implements OnInit, OnDestroy, OnChanges {

    protected ngUnsubscribe$: Subject<void> = new Subject<void>();

    @Input() workflowType: any = null;
    @Input() displayTriggerPopup: boolean = false;
    @Input() selectedTriggerTime: WorkflowModelsNS.WorkflowTimeDependentActions = null
    @Output() dialogClosed = new EventEmitter<boolean>();
    @Output() saveTriggerTime = new EventEmitter<WorkflowModelsNS.WorkflowTimeDependentActions>();

    startBaseFields = [
        {
            name: 'Execution Date',
            fieldName: 'ExecutionDateTime'
        }
    ];

    allAddOrSubstractBases = [
        { id: 2, name:"After" },
        { id: 1, name:"Before" },
    ];

    addOrSubstractBases = [
        { id: 2, name:"After" }
    ];

    occurrenceBases= [
        { id: WorkflowModelsNS.WorkflowOccurrenceTypsEnum.Years, name:"Years" },
        { id: WorkflowModelsNS.WorkflowOccurrenceTypsEnum.Months, name:"Months" },
        { id: WorkflowModelsNS.WorkflowOccurrenceTypsEnum.Days, name:"Days" },
        { id: WorkflowModelsNS.WorkflowOccurrenceTypsEnum.Hours, name:"Hours" },
        { id: WorkflowModelsNS.WorkflowOccurrenceTypsEnum.Minutes, name:"Minutes" },
        { id: WorkflowModelsNS.WorkflowOccurrenceTypsEnum.Seconds, name:"Seconds" },
    ];

    //Workflow details form
    triggerDialogHeader: 'Add Trigger Time'|'Edit Trigger Time' = 'Add Trigger Time';
    timeTriggerForm: FormGroup = new FormGroup({});
    isUnsavedData: boolean = false;
    triggerFormErrors: any = {};
    formErrorHandler: FormErrorHandler = new FormErrorHandler();

    constructor(
        private formBuilder: FormBuilder,
        public app: AppMainComponent,
    ) { }

    ngOnInit(): void {
        this.initTriggerTimeForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if(changes['selectedTriggerTime'] && changes['selectedTriggerTime']?.currentValue){
            this.selectedTriggerTime =  changes['selectedTriggerTime'].currentValue;
            this.setTriggerTimeEditData(this.selectedTriggerTime);
        }

        if(changes['workflowType'] && changes['workflowType']?.currentValue){
            this.workflowType = changes['workflowType']?.currentValue;

            if(this.workflowType?.allowBefore){
                this.addOrSubstractBases = ParseHelper.DeepCopy(this.allAddOrSubstractBases);
            }else{
                this.addOrSubstractBases = this.allAddOrSubstractBases.filter(c=>c.id==2);
            }
        }
    }

    ngOnDestroy(): void {
        if (this.ngUnsubscribe$) {
            this.ngUnsubscribe$.next();
            this.ngUnsubscribe$.complete();
            this.ngUnsubscribe$.unsubscribe();
        }
    }

    initTriggerTimeForm(){
        this.triggerDialogHeader = 'Add Trigger Time';
        this.triggerFormErrors = {};
        this.isUnsavedData = false;
        this.timeTriggerForm = this.formBuilder.group({
            startDateBaseField: [this.startBaseFields[0], [Validators.required]],
            selectedAddOrSubstractBase: [this.addOrSubstractBases[0], [Validators.required]],
            occurrenceValue: [null, [Validators.required, convertToAbsolute, convertToInteger]],
            occurrenceBase: [this.occurrenceBases[2], [Validators.required]]
        });

        this.formErrorHandler.handleErrors(this.timeTriggerForm, this.triggerFormErrors, {
            occurrenceValue: {
                required: "Enter start value",
            }
        });

        this.timeTriggerForm.valueChanges.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(e => this.isUnsavedData = true);
    }

    setTriggerTimeEditData(selectedTriggerTime: WorkflowModelsNS.WorkflowTimeDependentActions){
        this.triggerDialogHeader = 'Edit Trigger Time';
        this.timeTriggerForm.patchValue({
            selectedAddOrSubstractBase: this.addOrSubstractBases.find(c=>c.id===selectedTriggerTime.addOrSubstractBase)||this.addOrSubstractBases[0],
            occurrenceValue: selectedTriggerTime.occurrenceValue||null,
            occurrenceBase: this.occurrenceBases.find(c=>c.id===selectedTriggerTime.occurrenceBase)||this.occurrenceBases[2]
        });
    }

    saveTriggerTimeValue(){

        if (this.timeTriggerForm.invalid) {
            this.formErrorHandler.showErrorOnSubmit();
            this.scrollToError();
            return; //Return if form invalid
        }

        const triggerValue: WorkflowModelsNS.WorkflowTimeDependentActions = {
            executionOrders: ParseHelper.DeepCopy(WorkflowModelsNS.WorkflowActionDefaultOrders),
            addOrSubstractBase: this.timeTriggerForm.value.selectedAddOrSubstractBase.id,
            addOrSubstractBaseName: this.timeTriggerForm.value.selectedAddOrSubstractBase.name,
            dateTimeFieldName: this.timeTriggerForm.value.startDateBaseField.fieldName,
            dateTimeFieldLabel: this.timeTriggerForm.value.startDateBaseField.name,
            occurrenceBase: this.timeTriggerForm.value.occurrenceBase?.id,
            occurrenceBaseName: this.timeTriggerForm.value.occurrenceBase.name,
            occurrenceValue: this.timeTriggerForm.value.occurrenceValue
        }

        this.saveTriggerTime.emit(triggerValue);
        this.dialogClosed.emit(true);
    }

    dialogCLose(){
        this.initTriggerTimeForm();
        this.displayTriggerPopup = false;
        this.dialogClosed.emit(true);
    }

    // Scroll to error handler function
    scrollToError(): void {
        const firstElementWithError = document.querySelector('.ng-invalid[formControlName]');
        this.scrollTo(firstElementWithError);

        if (!firstElementWithError) {
            const secondElementWithError = document.querySelector('.p-invalid');
            this.scrollTo(secondElementWithError);
        }
    }

    // Scroll to error element
    scrollTo(el: Element): void {
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

}
